import PauseIcon from '@mui/icons-material/Pause';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { Box, CircularProgress, IconButton } from '@mui/material';
import { MouseEventHandler, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@work4all/components/lib/components/tooltip/Tooltip';

import { useStopwatchDuration } from './hooks/use-stopwatch-duration';

type Props = {
  isRunning: boolean;
  onStart?: MouseEventHandler<HTMLButtonElement>;
  onStop?: MouseEventHandler<HTMLButtonElement>;
  originalTime?: string;
};

export const StopwatchIconButton = ({
  isRunning,
  onStart,
  onStop,
  originalTime,
}: Props) => {
  const { t } = useTranslation();
  const duration = useStopwatchDuration(originalTime, !originalTime);

  const value = useMemo(() => {
    const seconds = duration?.seconds ?? 0;
    const percentage = (seconds / 60) * 100;

    return percentage;
  }, [duration]);

  if (isRunning)
    return (
      <Tooltip title={t('STOPWATCH.STOP')}>
        <IconButton
          type="button"
          onClick={onStop}
          size="small"
          sx={{
            pointerEvents: 'auto',
            cursor: 'pointer',
          }}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              size={20}
              variant="determinate"
              value={value}
              sx={{ color: 'var(--brand02)' }}
            />

            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PauseIcon
                sx={{
                  color: 'var(--brand02)',
                  fontSize: 12,
                }}
              />
            </Box>
          </Box>
        </IconButton>
      </Tooltip>
    );

  return (
    <Tooltip title={t('STOPWATCH.START')}>
      <IconButton type="button" onClick={onStart}>
        <TimerOutlinedIcon color="primary" />
      </IconButton>
    </Tooltip>
  );
};
